/**
 * The left navigation, as data.
 *
 * One declaration feeds three surfaces: the sidebar, the ⌘K palette's "Go to"
 * section and the breadcrumb trail. Each item carries the permission that makes
 * it real, and `visibleGroups()` is the single filter — a user without
 * `admin.user.list` does not see a greyed-out Users entry, they see no Users
 * entry (CLAUDE.md §6).
 *
 * WP0 (nav sharding, docs §19161): this file used to hold all 32 flat groups
 * inline — 3,685 lines, unreviewable and a merge-conflict magnet for every
 * packet that touched navigation at once. It is now a thin assembler: each
 * `nav/parts/*.ts` file owns exactly one FSD part (or, for `home`/`me`, one
 * pinned container above the numbered parts) and exports that part's
 * sections; this file imports every one of them and concatenates.
 *
 * Nothing here changed id, path, label or permission on a single item — see
 * each part file's own header for exactly which old top-level group(s) it
 * absorbed, and for the handful of groups (`finance`, `supply-chain`,
 * `revenue`, `delivery`) that were split by item-id prefix across two part
 * files, both halves still carrying the old group's id unchanged.
 */

import type { NavGroup, NavItem, NavPart } from './types'
export type { NavItem, NavGroup, NavPart } from './types'
export { navLabel, navGroupLabel, navPartLabel, navDescription, navKeywords } from './types'

import { HOME_GROUPS } from './parts/home'
import { ME_GROUPS } from './parts/me'
import { P01_ENTERPRISE_GROUPS } from './parts/p01-enterprise'
import { P02_FINANCE_GROUPS } from './parts/p02-finance'
import { P03_TREASURY_GROUPS } from './parts/p03-treasury'
import { P04_PROCUREMENT_GROUPS } from './parts/p04-procurement'
import { P05_SALES_GROUPS } from './parts/p05-sales'
import { P06_CRM_GROUPS } from './parts/p06-crm'
import { P07_INVENTORY_GROUPS } from './parts/p07-inventory'
import { P08_MANUFACTURING_GROUPS } from './parts/p08-manufacturing'
import { P09_HCM_GROUPS } from './parts/p09-hcm'
import { P10_POS_GROUPS } from './parts/p10-pos'
import { P11_PMS_GROUPS } from './parts/p11-pms'
import { P12_PROJECTS_GROUPS } from './parts/p12-projects'
import { P13_SERVICE_GROUPS } from './parts/p13-service'
import { P14_EAM_GROUPS } from './parts/p14-eam'
import { P15_REVENUE_MANAGEMENT_GROUPS } from './parts/p15-revenue'
import { P16_PLATFORM_GROUPS } from './parts/p16-platform'

/**
 * The pinned containers and the FSD's 16 numbered parts, in sidebar order.
 * No current consumer reads this — it is the level WP0 adds for the packets
 * that come after it (a part-headed sidebar, a part-scoped command palette
 * section) — but it is built from the same part-file exports `NAV_GROUPS`
 * below is, so the two can never drift from each other.
 */
export const NAV_PARTS: readonly NavPart[] = [
  { id: 'home', labelKey: 'nav.parts.home', sections: HOME_GROUPS },
  { id: 'me', labelKey: 'nav.parts.me', sections: ME_GROUPS },
  { id: 'p01-enterprise', labelKey: 'nav.parts.enterprise', sections: P01_ENTERPRISE_GROUPS },
  { id: 'p02-finance', labelKey: 'nav.parts.finance', sections: P02_FINANCE_GROUPS },
  { id: 'p03-treasury', labelKey: 'nav.parts.treasury', sections: P03_TREASURY_GROUPS },
  { id: 'p04-procurement', labelKey: 'nav.parts.procurement', sections: P04_PROCUREMENT_GROUPS },
  { id: 'p05-sales', labelKey: 'nav.parts.sales', sections: P05_SALES_GROUPS },
  { id: 'p06-crm', labelKey: 'nav.parts.crm', sections: P06_CRM_GROUPS },
  { id: 'p07-inventory', labelKey: 'nav.parts.inventory', sections: P07_INVENTORY_GROUPS },
  { id: 'p08-manufacturing', labelKey: 'nav.parts.manufacturing', sections: P08_MANUFACTURING_GROUPS },
  { id: 'p09-hcm', labelKey: 'nav.parts.hcm', sections: P09_HCM_GROUPS },
  { id: 'p10-pos', labelKey: 'nav.parts.pointOfSale', sections: P10_POS_GROUPS },
  { id: 'p11-pms', labelKey: 'nav.parts.propertyManagement', sections: P11_PMS_GROUPS },
  { id: 'p12-projects', labelKey: 'nav.parts.projects', sections: P12_PROJECTS_GROUPS },
  { id: 'p13-service', labelKey: 'nav.parts.service', sections: P13_SERVICE_GROUPS },
  { id: 'p14-eam', labelKey: 'nav.parts.eam', sections: P14_EAM_GROUPS },
  { id: 'p15-revenue', labelKey: 'nav.parts.revenueManagement', sections: P15_REVENUE_MANAGEMENT_GROUPS },
  { id: 'p16-platform', labelKey: 'nav.parts.platform', sections: P16_PLATFORM_GROUPS },
]

/**
 * The flattened list every current consumer (`AppSidebar.vue`,
 * `CommandPaletteHost.vue` via `useCommands.ts`, `useNavPrefs.ts`,
 * `AppBreadcrumbs.vue`, `items.test.ts`, `keys.test.ts`) already expects.
 * Derived from `NAV_PARTS` rather than declared separately, so the two views
 * of the same data cannot disagree.
 */
export const NAV_GROUPS: readonly NavGroup[] = NAV_PARTS.flatMap(
  (part) => part.sections,
)

export type PermissionCheck = (permission: string) => boolean

/** Groups with their items filtered by permission; empty groups are dropped. */
export function visibleGroups(
  groups: readonly NavGroup[],
  can: PermissionCheck,
): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.permission === undefined || can(item.permission)),
    }))
    .filter((group) => group.items.length > 0)
}

/**
 * Parts with their sections filtered by permission the same way
 * `visibleGroups` filters groups: drop items the user can't reach, then drop
 * any section left with zero items, then drop any part left with zero
 * sections. Used by `AppSidebar.vue` to render the FSD's 16-part heading
 * structure; every other consumer keeps reading the flat `NAV_GROUPS`/
 * `visibleGroups` pair this is layered on top of.
 */
export function visibleParts(
  parts: readonly NavPart[],
  can: PermissionCheck,
): NavPart[] {
  return parts
    .map((part) => ({
      ...part,
      sections: visibleGroups(part.sections, can),
    }))
    .filter((part) => part.sections.length > 0)
}

/** Every visible item, flattened — what the palette and favourites work from. */
export function visibleItems(
  groups: readonly NavGroup[],
  can: PermissionCheck,
): NavItem[] {
  return visibleGroups(groups, can).flatMap((group) => group.items)
}

/** Find an item by its route path, for breadcrumbs and recents. */
export function itemForPath(path: string): NavItem | undefined {
  const candidates = NAV_GROUPS.flatMap((group) => group.items)
  // Longest match first so `/users/1` resolves to Users, not to Dashboard.
  return [...candidates]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => (item.to === '/' ? path === '/' : path.startsWith(item.to)))
}
